import { gql, useMutation, useQuery } from '@apollo/client';
import { useCheckAccountIdInCommonConfig } from '../../../../graphql/queries/checkAccountIdInCommonConfig';

const LIST_UNDELIVERABLE_UPLOAD_TASKS_PAGE = gql`
  query ListUndeliverableUploadTasksPage(
    $fileName: String
    $uploaderAccountId: Long
    $uploadTime: TimeRangeTypeInput
    $page: Int!
    $size: Int!
  ) {
    listUndeliverableUploadTasksPage(
      fileName: $fileName
      uploaderAccountId: $uploaderAccountId
      uploadTime: $uploadTime
      page: $page
      size: $size
    ) {
      totalElements
      content {
        taskNo
        fileObjectKey
        originalFileName
        createdBy
        uploaderName
        uploadTime
        rowCount
        legacyGroup
      }
    }
  }
`;

const LIST_UNDELIVERABLE_UPLOAD_UPLOADER_OPTIONS = gql`
  query ListUndeliverableUploadUploaderOptions($fileName: String, $uploadTime: TimeRangeTypeInput) {
    listUndeliverableUploadUploaderOptions(fileName: $fileName, uploadTime: $uploadTime) {
      accountId
      displayName
    }
  }
`;

const PREVIEW_UNDELIVERABLE_UPLOAD_BY_FILE = gql`
  mutation PreviewUndeliverableUploadByFile($fileBase64: String!, $fileName: String!) {
    previewUndeliverableUploadByFile(fileBase64: $fileBase64, fileName: $fileName) {
      totalCount
      validCount
      invalidCount
      validPayloadFingerprint
      errors {
        rowNo
        message
      }
    }
  }
`;

const CONFIRM_UNDELIVERABLE_UPLOAD_BY_FILE = gql`
  mutation ConfirmUndeliverableUploadByFile(
    $fileBase64: String!
    $fileName: String!
    $clientExpectedValidCount: Int!
    $clientExpectedValidPayloadFingerprint: String!
  ) {
    confirmUndeliverableUploadByFile(
      fileBase64: $fileBase64
      fileName: $fileName
      clientExpectedValidCount: $clientExpectedValidCount
      clientExpectedValidPayloadFingerprint: $clientExpectedValidPayloadFingerprint
    ) {
      fileObjectKey
      insertedCount
    }
  }
`;

const SUBMIT_UNDELIVERABLE_UPLOAD_DETAIL_FILES_TO_DOWNLOAD_CENTER = gql`
  mutation SubmitUndeliverableUploadDetailFilesToDownloadCenter(
    $tasks: [UndeliverableUploadTaskKeyInput!]!
    $timezone: String
  ) {
    submitUndeliverableUploadDetailFilesToDownloadCenter(tasks: $tasks, timezone: $timezone)
  }
`;

type TimeRangeTypeInput = {
  start: string;
  end: string;
};

export type UndeliverableUploadOrderFilterVariables = {
  fileName?: string | null;
  uploaderAccountId?: number | null;
  uploadTime?: TimeRangeTypeInput | null;
};

export type UndeliverableUploadTasksPageVariables = UndeliverableUploadOrderFilterVariables & {
  page: number;
  size: number;
};

export type UndeliverableUploadUploaderOptionsVariables = {
  fileName?: string | null;
  uploadTime?: TimeRangeTypeInput | null;
};

export type UndeliverableUploadPreviewValidationError = {
  rowNo: number;
  message: string;
};

export type UndeliverableUploadPreviewResult = {
  totalCount: number;
  validCount: number;
  invalidCount: number;
  validPayloadFingerprint: string;
  errors: UndeliverableUploadPreviewValidationError[];
};

export const useUndeliverableUploadQueries = (
  tasksPageVariables: UndeliverableUploadTasksPageVariables,
  uploaderOptionsVariables: UndeliverableUploadUploaderOptionsVariables
) => {
  const permission = useCheckAccountIdInCommonConfig('qc.undeliverable_detail.upload.account_ids');
  const tasksPageQuery = useQuery(LIST_UNDELIVERABLE_UPLOAD_TASKS_PAGE, {
    variables: tasksPageVariables,
  });
  const uploaderOptionsQuery = useQuery(LIST_UNDELIVERABLE_UPLOAD_UPLOADER_OPTIONS, {
    variables: uploaderOptionsVariables,
  });
  return {
    permission,
    tasksPageQuery,
    uploaderOptionsQuery,
  };
};

export type UndeliverableUploadTaskKeyInput = {
  taskNo?: string | null;
  legacyFileObjectKey?: string | null;
  legacyCreatedBy?: string | null;
};

export const useUndeliverableUploadMutations = () => {
  const [previewByFileMutation] = useMutation(PREVIEW_UNDELIVERABLE_UPLOAD_BY_FILE);
  const [confirmByFileMutation] = useMutation(CONFIRM_UNDELIVERABLE_UPLOAD_BY_FILE);
  const [submitDetailFilesToDownloadCenterMutation] = useMutation<
    { submitUndeliverableUploadDetailFilesToDownloadCenter: string[] },
    { tasks: UndeliverableUploadTaskKeyInput[]; timezone?: string | null }
  >(SUBMIT_UNDELIVERABLE_UPLOAD_DETAIL_FILES_TO_DOWNLOAD_CENTER);
  return {
    previewByFileMutation,
    confirmByFileMutation,
    submitDetailFilesToDownloadCenterMutation,
  };
};
